import { useCallback, useEffect, useMemo, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import themePlugin from '@fullcalendar/react/themes/classic';
import interactionPlugin from '@fullcalendar/react/interaction';
import dayGridPlugin from '@fullcalendar/react/daygrid';
import timeGridPlugin from '@fullcalendar/react/timegrid';
import listPlugin from '@fullcalendar/react/list';
import ptBrLocale from '@fullcalendar/react/locales/pt-br';
import '@fullcalendar/react/skeleton.css';
import '@fullcalendar/react/themes/classic/theme.css';
import '@fullcalendar/react/themes/classic/palette.css';
import {
  CalendarDays,
  CalendarPlus,
  Clock3,
  ListChecks,
  RefreshCw,
  Settings2,
  UserRound,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuthSession } from '../../../../contexts/AuthSessionContext.js';
import AppointmentModal from '../components/agenda/AppointmentModal.tsx';
import ServicesPanel from '../components/agenda/ServicesPanel.tsx';
import ProfessionalsPanel from '../components/agenda/ProfessionalsPanel.tsx';
import AgendaSettingsPanel from '../components/agenda/AgendaSettingsPanel.tsx';
import {
  fetchAgendaAppointments,
  fetchAgendaConfig,
  fetchAgendaProfessionals,
  fetchAgendaServices,
  updateAgendaAppointment,
} from '../services/api.ts';
import type {
  AgendaAppointment,
  AgendaConfig,
  AgendaProfessional,
  AgendaService,
} from '../types';
import './Agenda.css';

type AgendaTab = 'agenda' | 'services' | 'professionals' | 'settings';

const STATUS_LABELS: Record<string, string> = {
  scheduled: 'Agendada',
  confirmed: 'Confirmada',
  completed: 'Concluída',
  cancelled: 'Cancelada',
  no_show: 'Não compareceu',
};

const toSlotDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(remaining).padStart(2, '0')}:00`;
};

const DEFAULT_VISIBLE_START_HOUR = 6;
const DEFAULT_VISIBLE_END_HOUR = 24;
const APPOINTMENTS_POLL_INTERVAL_MS = 10000;

const toCalendarHour = (hour: number) => `${String(hour).padStart(2, '0')}:00:00`;

const toHourLabel = (hour: number) => `${String(hour % 24).padStart(2, '0')}:00`;

const RESIZE_OBSERVER_LOOP_MESSAGES = new Set([
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded',
]);

const Agenda = () => {
  const { isAdmin, isOwner, atendabotAtendente, hasProductAccess } = useAuthSession();
  const isManager = Boolean(
    isAdmin
    || isOwner
    || (!atendabotAtendente && hasProductAccess('atendabot')),
  );
  const [activeTab, setActiveTab] = useState<AgendaTab>('agenda');
  const [config, setConfig] = useState<AgendaConfig | null>(null);
  const [services, setServices] = useState<AgendaService[]>([]);
  const [professionals, setProfessionals] = useState<AgendaProfessional[]>([]);
  const [appointments, setAppointments] = useState<AgendaAppointment[]>([]);
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [professionalFilter, setProfessionalFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<AgendaAppointment | null>(null);
  const [initialStart, setInitialStart] = useState<Date | null>(null);
  const [visibleStartHour, setVisibleStartHour] = useState(DEFAULT_VISIBLE_START_HOUR);
  const [visibleEndHour, setVisibleEndHour] = useState(DEFAULT_VISIBLE_END_HOUR);

  const loadCore = useCallback(async () => {
    setLoadError('');
    try {
      const [nextConfig, nextServices, nextProfessionals] = await Promise.all([
        fetchAgendaConfig(),
        fetchAgendaServices(true),
        fetchAgendaProfessionals(true),
      ]);
      setConfig(nextConfig);
      setServices(nextServices);
      setProfessionals(nextProfessionals);
    } catch (error: any) {
      setLoadError(error?.response?.data?.error || 'Não foi possível carregar a configuração da agenda.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAppointments = useCallback(async (silent = false) => {
    if (!range) return;
    if (!silent) setLoadingAppointments(true);
    try {
      const data = await fetchAgendaAppointments({
        ...range,
        ...(professionalFilter ? { professionalId: professionalFilter } : {}),
        ...(serviceFilter ? { serviceId: serviceFilter } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
      });
      setAppointments(data);
    } catch (error: any) {
      if (!silent) toast.error(error?.response?.data?.error || 'Não foi possível atualizar a agenda.');
    } finally {
      if (!silent) setLoadingAppointments(false);
    }
  }, [professionalFilter, range, serviceFilter, statusFilter]);

  useEffect(() => {
    void loadCore();
  }, [loadCore]);

  useEffect(() => {
    const ignoreResizeObserverLoop = (event: ErrorEvent) => {
      if (RESIZE_OBSERVER_LOOP_MESSAGES.has(event.message)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    window.addEventListener('error', ignoreResizeObserverLoop, true);
    return () => window.removeEventListener('error', ignoreResizeObserverLoop, true);
  }, []);

  useEffect(() => {
    void loadAppointments();
  }, [loadAppointments]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void loadAppointments(true);
    }, APPOINTMENTS_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [loadAppointments]);

  useEffect(() => {
    if (!isManager && activeTab !== 'agenda') setActiveTab('agenda');
  }, [activeTab, isManager]);

  const activeProfessionals = useMemo(() => professionals.filter((professional) => professional.active), [professionals]);
  const hasWeeklyAvailability = useMemo(() => activeProfessionals.some((professional) => (
    professional.availabilityConfigured
  )), [activeProfessionals]);
  const calendarEvents = useMemo(() => appointments.map((appointment) => ({
    id: appointment.id,
    title: `${appointment.client.name} · ${appointment.service.name}`,
    start: appointment.startAt,
    end: appointment.endAt,
    backgroundColor: appointment.professional.color || '#322F84',
    borderColor: appointment.professional.color || '#322F84',
    editable: appointment.status === 'scheduled' || appointment.status === 'confirmed',
    extendedProps: { appointment },
    classNames: [`agenda-event-${appointment.status}`],
  })), [appointments]);

  const nextAppointment = useMemo(() => appointments.find((appointment) => (
    new Date(appointment.startAt) >= new Date()
    && (appointment.status === 'scheduled' || appointment.status === 'confirmed')
  )), [appointments]);

  const openCreate = (date: Date) => {
    const intervalMs = Math.max(5, config?.slotIntervalMinutes || 15) * 60 * 1000;
    const roundedDate = new Date(Math.ceil(date.getTime() / intervalMs) * intervalMs);
    setSelectedAppointment(null);
    setInitialStart(roundedDate);
    setModalOpen(true);
  };

  const openAppointment = (appointment: AgendaAppointment) => {
    setSelectedAppointment(appointment);
    setInitialStart(null);
    setModalOpen(true);
  };

  const handleEventDrop = async (info: any) => {
    const appointment = info.event.extendedProps.appointment as AgendaAppointment;
    const nextStart = info.event.start?.toISOString();
    if (!nextStart) {
      info.revert();
      return;
    }
    try {
      await updateAgendaAppointment(appointment.id, { startAt: nextStart });
      toast.success('Agendamento reagendado.');
      await loadAppointments(true);
    } catch (error: any) {
      info.revert();
      openAppointment(appointment);
      const details = error?.response?.data?.details;
      const firstAlternative = details?.alternatives?.[0];
      const suffix = firstAlternative && config
        ? ` Próxima opção: ${new Date(firstAlternative.startAt).toLocaleString('pt-BR', { timeZone: config.timezone })}.`
        : '';
      toast.error(`${error?.response?.data?.error || 'Não foi possível reagendar.'}${suffix}`);
    }
  };

  const handleSaved = async (appointment: AgendaAppointment) => {
    setModalOpen(false);
    setSelectedAppointment(null);
    setAppointments((current) => {
      const exists = current.some((item) => item.id === appointment.id);
      return exists
        ? current.map((item) => (item.id === appointment.id ? appointment : item))
        : [...current, appointment];
    });
    await loadAppointments(true);
  };

  const refreshAll = async () => {
    setLoadingAppointments(true);
    await Promise.all([loadCore(), loadAppointments(true)]);
    setLoadingAppointments(false);
  };

  if (loading) {
    return <div className="agenda-page"><div className="agenda-loading"><RefreshCw className="spin" /> Carregando agenda…</div></div>;
  }

  if (loadError || !config) {
    return (
      <div className="agenda-page">
        <div className="agenda-empty-state error">
          <CalendarDays size={32} />
          <strong>Não foi possível abrir a agenda</strong>
          <p>{loadError}</p>
          <button type="button" className="agenda-button primary" onClick={() => void loadCore()}>Tentar novamente</button>
        </div>
      </div>
    );
  }

  const tabs: Array<{ id: AgendaTab; label: string; icon: JSX.Element }> = [
    { id: 'agenda', label: 'Agenda', icon: <CalendarDays size={17} /> },
    ...(isManager ? [
      { id: 'services' as AgendaTab, label: 'Serviços', icon: <ListChecks size={17} /> },
      { id: 'professionals' as AgendaTab, label: 'Profissionais', icon: <UserRound size={17} /> },
      { id: 'settings' as AgendaTab, label: 'Bloqueios e configurações', icon: <Settings2 size={17} /> },
    ] : []),
  ];

  return (
    <div className="agenda-page">
      <header className="agenda-page-header">
        <div>
          <h1>Agenda</h1>
        </div>
        <div className="agenda-header-actions">
          <button type="button" className="agenda-button primary" onClick={() => openCreate(new Date())} disabled={activeProfessionals.length === 0}>
            <CalendarPlus size={17} /> Novo agendamento
          </button>
        </div>
      </header>

      {isManager && activeProfessionals.length === 0 && (
        <aside className="agenda-setup-notice" aria-label="Configuração necessária">
          <UserRound size={19} aria-hidden="true" />
          <p><strong>Cadastre pelo menos um profissional</strong> para começar a usar a agenda.</p>
          <button type="button" onClick={() => setActiveTab('professionals')}>Cadastrar profissional</button>
        </aside>
      )}

      <nav className="agenda-tabs" aria-label="Seções da agenda">
        {tabs.map((tab) => (
          <button key={tab.id} type="button" className={activeTab === tab.id ? 'active' : ''} onClick={() => setActiveTab(tab.id)}>
            {tab.icon}{tab.label}
          </button>
        ))}
      </nav>

      {activeTab === 'agenda' && (
        <div className="agenda-workspace">
          <section className="agenda-summary-row">
            <article><CalendarDays size={20} /><div><small>No período visível</small><strong>{appointments.length}</strong></div></article>
            <article><Clock3 size={20} /><div><small>Próximo agendamento</small><strong>{nextAppointment ? new Date(nextAppointment.startAt).toLocaleString('pt-BR', { timeZone: config.timezone, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'Nenhum'}</strong></div></article>
            <article><UserRound size={20} /><div><small>Profissionais ativos</small><strong>{activeProfessionals.length}</strong></div></article>
          </section>

          <section className="agenda-panel agenda-calendar-panel">
            {activeProfessionals.length > 0 && !hasWeeklyAvailability && (
              <div className="agenda-alert agenda-alert-info">
                Nenhum horário semanal foi configurado. Somente disponibilidades extras poderão gerar horários.
              </div>
            )}
            <div className="agenda-filters">
              <label><span>Profissional</span><select value={professionalFilter} onChange={(event) => setProfessionalFilter(event.target.value)}><option value="">Todos</option>{professionals.map((item) => <option key={item.id} value={item.id}>{item.name}{item.active ? '' : ' · inativo'}</option>)}</select></label>
              <label><span>Serviço</span><select value={serviceFilter} onChange={(event) => setServiceFilter(event.target.value)}><option value="">Todos</option>{services.map((item) => <option key={item.id} value={item.id}>{item.name}{item.active ? '' : ' · inativo'}</option>)}</select></label>
              <label><span>Status</span><select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="">Todos</option>{Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
              <div className="agenda-legend">{professionals.map((item) => <span key={item.id}><i style={{ backgroundColor: item.color }} />{item.name}{item.active ? '' : ' (inativo)'}</span>)}</div>
            </div>

            <div className="agenda-hours-controls" aria-label="Horários visíveis na agenda">
              <span>Horários: {toHourLabel(visibleStartHour)}–{toHourLabel(visibleEndHour)}</span>
              <div className="agenda-hours-control-group" aria-label="Ajustar início do dia">
                <strong>Início</strong>
                <button
                  type="button"
                  onClick={() => setVisibleStartHour((hour) => Math.max(0, hour - 1))}
                  disabled={visibleStartHour === 0}
                >
                  mais
                </button>
                <button
                  type="button"
                  onClick={() => setVisibleStartHour((hour) => Math.min(visibleEndHour - 1, hour + 1))}
                  disabled={visibleStartHour >= visibleEndHour - 1}
                >
                  menos
                </button>
              </div>
              <div className="agenda-hours-control-group" aria-label="Ajustar fim do dia">
                <strong>Fim</strong>
                <button
                  type="button"
                  onClick={() => setVisibleEndHour((hour) => Math.max(visibleStartHour + 1, hour - 1))}
                  disabled={visibleEndHour <= visibleStartHour + 1}
                >
                  menos
                </button>
                <button
                  type="button"
                  onClick={() => setVisibleEndHour((hour) => Math.min(24, hour + 1))}
                  disabled={visibleEndHour === 24}
                >
                  mais
                </button>
              </div>
            </div>

            {activeProfessionals.length === 0 ? (
              <div className="agenda-empty-state"><UserRound size={30} /><strong>Agenda ainda não configurada</strong><p>Cadastre pelo menos um profissional para começar a usar a agenda.</p></div>
            ) : (
              <div className={`agenda-calendar ${loadingAppointments ? 'is-loading' : ''}`}>
                <FullCalendar
                  plugins={[themePlugin, interactionPlugin, dayGridPlugin, timeGridPlugin, listPlugin]}
                  locale={ptBrLocale}
                  timeZone={config.timezone}
                  initialView={typeof window !== 'undefined' && window.innerWidth < 768 ? 'listDay' : 'timeGridWeek'}
                  headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek' }}
                  buttonText={{ today: 'Hoje', month: 'Mês', week: 'Semana', day: 'Dia', list: 'Lista' }}
                  firstDay={1}
                  nowIndicator
                  selectable
                  selectMirror
                  editable
                  eventDurationEditable={false}
                  allDaySlot={false}
                  height="auto"
                  slotDuration={toSlotDuration(config.slotIntervalMinutes)}
                  slotLabelInterval="01:00:00"
                  slotMinTime={toCalendarHour(visibleStartHour)}
                  slotMaxTime={toCalendarHour(visibleEndHour)}
                  events={calendarEvents}
                  datesSet={(info: any) => setRange({ from: info.start.toISOString(), to: info.end.toISOString() })}
                  dateClick={(info: any) => openCreate(info.date)}
                  select={(info: any) => {
                    openCreate(info.start);
                    info.view.calendar.unselect();
                  }}
                  eventClick={(info: any) => openAppointment(info.event.extendedProps.appointment)}
                  eventDrop={handleEventDrop}
                  eventContent={(info: any) => {
                    const appointment = info.event.extendedProps.appointment as AgendaAppointment;
                    return (
                      <div className="agenda-event-content">
                        <strong>{info.timeText} {appointment.client.name}</strong>
                        <span>{appointment.service.name}</span>
                        <small>{appointment.professional.name} · {STATUS_LABELS[appointment.status]}</small>
                      </div>
                    );
                  }}
                  noEventsContent="Nenhum agendamento neste período"
                />
              </div>
            )}
          </section>
        </div>
      )}

      {activeTab === 'services' && isManager && <ServicesPanel services={services} onChanged={refreshAll} />}
      {activeTab === 'professionals' && isManager && <ProfessionalsPanel professionals={professionals} services={services} onChanged={refreshAll} />}
      {activeTab === 'settings' && isManager && <AgendaSettingsPanel config={config} professionals={professionals} onConfigChanged={refreshAll} />}

      <AppointmentModal
        open={modalOpen}
        appointment={selectedAppointment}
        initialStart={initialStart}
        timezone={config.timezone}
        services={services}
        professionals={professionals}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
      />
    </div>
  );
};

export default Agenda;
